package com.swetdot.swetgram.ui.shop

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.swetdot.swetgram.data.api.ApiError
import com.swetdot.swetgram.data.shop.XpShopRepository
import com.swetdot.swetgram.data.tasks.TasksRepository
import com.swetdot.swetgram.domain.XpShopItemDomain
import com.swetdot.swetgram.protocol.dto.ErrorCodes
import com.swetdot.swetgram.ui.text.UiText
import com.swetdot.swetgram.ui.text.text
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch
import swetgram.shared.generated.resources.Res
import swetgram.shared.generated.resources.shop_error_load
import swetgram.shared.generated.resources.shop_insufficient_xp
import swetgram.shared.generated.resources.shop_max_freezes
import swetgram.shared.generated.resources.shop_purchase_success

data class XpShopUiState(
    val items: List<XpShopItemDomain> = emptyList(),
    val spendableXp: Int = 0,
    val streakFreezes: Int = 0,
    val loading: Boolean = false,
    val purchasingCode: String? = null,
    val confirmingItem: XpShopItemDomain? = null,
    val notice: UiText? = null,
    val error: UiText? = null,
)

class XpShopViewModel(
    private val shop: XpShopRepository,
    private val tasks: TasksRepository,
) : ViewModel() {

    private val _state = MutableStateFlow(XpShopUiState())
    val state: StateFlow<XpShopUiState> = _state.asStateFlow()

    init {
        load()
    }

    fun load() {
        _state.value = _state.value.copy(loading = true, error = null)
        viewModelScope.launch {
            try {
                val catalog = shop.getCatalog()
                _state.value = _state.value.copy(
                    items = catalog.items,
                    spendableXp = catalog.spendableXp,
                    streakFreezes = catalog.streakFreezes,
                    loading = false,
                )
            } catch (cancellation: CancellationException) {
                throw cancellation
            } catch (_: Throwable) {
                _state.value = _state.value.copy(
                    loading = false,
                    error = Res.string.shop_error_load.text(),
                )
            }
        }
    }

    fun askConfirmPurchase(item: XpShopItemDomain) {
        if (_state.value.spendableXp < item.priceXp) {
            _state.value = _state.value.copy(error = Res.string.shop_insufficient_xp.text())
            return
        }
        _state.value = _state.value.copy(confirmingItem = item, error = null)
    }

    fun dismissConfirm() {
        _state.value = _state.value.copy(confirmingItem = null)
    }

    fun confirmPurchase() {
        val item = _state.value.confirmingItem ?: return
        _state.value = _state.value.copy(
            confirmingItem = null,
            purchasingCode = item.code,
            error = null,
        )

        viewModelScope.launch {
            try {
                val purchase = shop.purchase(item.code)
                val catalog = runCatching { shop.getCatalog() }.getOrNull()
                _state.value = _state.value.copy(
                    items = catalog?.items ?: _state.value.items,
                    spendableXp = purchase.remainingXp,
                    streakFreezes = purchase.streakFreezes,
                    purchasingCode = null,
                    notice = Res.string.shop_purchase_success.text(item.title),
                )
            } catch (cancellation: CancellationException) {
                throw cancellation
            } catch (error: ApiError) {
                val code = (error as? ApiError.Conflict)?.code
                    ?: (error as? ApiError.Validation)?.code
                    ?: (error as? ApiError.Forbidden)?.code
                    ?: (error as? ApiError.Server)?.code
                val uiError = when (code) {
                    ErrorCodes.INSUFFICIENT_XP -> Res.string.shop_insufficient_xp.text()
                    ErrorCodes.MAX_FREEZES_REACHED -> Res.string.shop_max_freezes.text()
                    else -> Res.string.shop_error_load.text()
                }
                _state.value = _state.value.copy(
                    purchasingCode = null,
                    error = uiError,
                )
            } catch (_: Throwable) {
                _state.value = _state.value.copy(
                    purchasingCode = null,
                    error = Res.string.shop_error_load.text(),
                )
            }
        }
    }

    fun dismissNotice() {
        _state.value = _state.value.copy(notice = null)
    }

    fun dismissError() {
        _state.value = _state.value.copy(error = null)
    }
}
